import type { SseSubscription } from '../../common/services/fetchSse';

// BFF Adapter — the abstract transport contract the host shell programs against.
//
// The host never knows whether the BFF is ESA over REST, a GraphQL endpoint,
// gRPC-Web, or something entirely different. It only talks to this interface.
// Different product lines plug in different concrete implementations (or
// different configurations of the same implementation) via the AdapterRegistry.
//
// The BFF adapter internally handles auth-token injection by receiving an
// AuthAdapter at construction time — keeping Auth and BFF independently
// pluggable.

export interface BffRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeout?: number;
  /** Override the axios responseType. Use 'blob' for binary downloads. Defaults to 'json'. */
  responseType?: 'blob' | 'json' | 'text';
}

export class BffError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown
  ) {
    super(`BFF request failed: ${status} ${statusText}`);
    this.name = 'BffError';
  }
}

/**
 * The contract every BFF adapter must implement. A product line binds one
 * concrete adapter (e.g. RestBffAdapter) with its own config.
 */
export interface BffAdapter {
  /** Human-readable name for diagnostics / logging. */
  readonly name: string;

  /** Make an authenticated request to the BFF. */
  request<T = unknown>(path: string, options?: BffRequestOptions): Promise<T>;

  /** Execute a GraphQL document (optional — GraphqlBffAdapter only). */
  query?<T>(document: string, variables?: Record<string, unknown>): Promise<T>;

  /** Open an authenticated SSE stream (DR-04, §8). */
  openEventStream(path: string): SseSubscription;

  /**
   * Register the callback invoked when a token refresh fails and the user must
   * be force-logged-out.  Replaces any prior handler (idempotent registration).
   * Handler must be safe to call multiple times.
   */
  setForceLogoutHandler?(handler: () => void): void;
}
